/* ==========================================================================
   FNDRY — Cart drawer
   Plain JS, no framework. All clicks are delegated to document, so the
   content swap on re-render never needs to rebind anything.

   Cart event contract (DESIGN-SPEC "Cart events"):
   - dispatches fndry:cart:refresh on removal, so every quick-add on the
     page re-derives its added state from /cart.js
   - dispatches fndry:cart:updated { cart, variantId, source } on a
     quantity change
   - listens for both, so an add from any quick-add re-renders the drawer
   ========================================================================== */

(function () {
  'use strict';

  if (window.__fndryCartDrawer) return; /* editor re-injects can re-run this file */
  window.__fndryCartDrawer = true;

  var SECTION = 'fndry-cart-drawer';
  var busy = false;
  var suppressRefresh = false;
  var opener = null;

  function root() {
    return document.querySelector('[data-fndry-cart-drawer]');
  }

  function content() {
    var r = root();
    return r ? r.querySelector('[data-fcd-content]') : null;
  }

  function panel() {
    var r = root();
    return r ? r.querySelector('[data-fcd-panel]') : null;
  }

  function rootUrl() {
    var r = root();
    if (r && r.dataset.rootUrl) return r.dataset.rootUrl;
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function isOpen() {
    var r = root();
    return !!r && r.classList.contains('is-open');
  }

  /* ---------- header count bubble ---------------------------------------- */

  function syncBubble(count) {
    document.querySelectorAll('[data-fndry-cart-open]').forEach(function (link) {
      var label = link.querySelector('[data-fndry-cart-label]');
      if (label) label.textContent = 'Cart, ' + count + ' items';

      var bubble = link.querySelector('.fndry-cart-count');
      if (!bubble) {
        bubble = document.createElement('span');
        bubble.className = 'fndry-cart-count';
        bubble.setAttribute('aria-hidden', 'true');
        link.appendChild(bubble);
      }
      bubble.textContent = count;
      bubble.hidden = count === 0;
    });
  }

  /* ---------- open / close ------------------------------------------------ */

  function open() {
    var r = root();
    if (!r || isOpen()) return;

    opener = document.activeElement;
    r.classList.add('is-open');
    var p = panel();
    if (p) p.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fndry-no-scroll');

    window.setTimeout(function () {
      var close = r.querySelector('[data-fcd-close]');
      if (close) close.focus();
    }, 120);
  }

  function close() {
    var r = root();
    if (!r || !isOpen()) return;

    r.classList.remove('is-open');
    var p = panel();
    if (p) p.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fndry-no-scroll');

    if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
      opener.focus();
    }
    opener = null;
  }

  /* ---------- rendering --------------------------------------------------- */

  function render(html) {
    var current = content();
    if (!current) return;

    var holder = document.createElement('div');
    holder.innerHTML = html;
    var fresh = holder.querySelector('[data-fcd-content]');
    if (!fresh) return;

    current.replaceWith(fresh);
    syncBubble(parseInt(fresh.dataset.itemCount, 10) || 0);

    /* the swap drops focus to body — put it back inside the dialog */
    if (isOpen() && !panel().contains(document.activeElement)) {
      var focusTarget = fresh.querySelector('[data-fcd-close]');
      if (focusTarget) focusTarget.focus();
    }
  }

  function refresh() {
    return fetch(rootUrl() + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data && data[SECTION]) render(data[SECTION]);
      })
      .catch(function () {});
  }

  /* ---------- line changes ------------------------------------------------ */

  function changeLine(key, quantity, variantId) {
    if (busy || !key) return;
    busy = true;

    var r = root();
    var c = content();
    if (c) c.setAttribute('aria-busy', 'true');

    var changeUrl = (r && r.dataset.cartChangeUrl) || '/cart/change';

    fetch(changeUrl + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart change failed');
        return response.json();
      })
      .then(function (cart) {
        if (cart.sections && cart.sections[SECTION]) render(cart.sections[SECTION]);

        if (quantity === 0) {
          /* removal: the contract says refresh — every quick-add re-derives
             its state from /cart.js. Our own listener skips the duplicate. */
          suppressRefresh = true;
          document.dispatchEvent(new CustomEvent('fndry:cart:refresh'));
          suppressRefresh = false;
        } else {
          document.dispatchEvent(
            new CustomEvent('fndry:cart:updated', {
              detail: { cart: cart, variantId: variantId, source: 'fndry-cart-drawer' }
            })
          );
        }
      })
      .catch(function () {
        refresh();
      })
      .finally(function () {
        busy = false;
        var after = content();
        if (after) after.removeAttribute('aria-busy');
      });
  }

  /* ---------- delegated clicks -------------------------------------------- */

  document.addEventListener('click', function (event) {
    var openLink = event.target.closest('[data-fndry-cart-open]');
    if (openLink && root()) {
      event.preventDefault();
      open();
      return;
    }

    var r = root();
    if (!r || !r.contains(event.target)) return;

    if (event.target.closest('[data-fcd-close]') || event.target.closest('[data-fcd-scrim]')) {
      close();
      return;
    }

    var qtyBtn = event.target.closest('[data-fcd-qty]');
    if (qtyBtn) {
      var current = parseInt(qtyBtn.dataset.quantity, 10) || 0;
      var next = qtyBtn.dataset.fcdQty === 'up' ? current + 1 : current - 1;
      changeLine(qtyBtn.dataset.key, Math.max(0, next), parseInt(qtyBtn.dataset.variantId, 10) || null);
      return;
    }

    var removeBtn = event.target.closest('[data-fcd-remove]');
    if (removeBtn) {
      changeLine(removeBtn.dataset.key, 0, parseInt(removeBtn.dataset.variantId, 10) || null);
    }
  });

  /* ---------- keyboard: escape + focus containment ------------------------ */

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    var p = panel();
    if (!p) return;

    var focusables = p.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (!p.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---------- cart event contract ----------------------------------------- */

  document.addEventListener('fndry:cart:updated', function (event) {
    var detail = event.detail || {};
    if (detail.source === 'fndry-cart-drawer') return;

    refresh().then(function () {
      var r = root();
      if (r && r.dataset.openOnAdd !== 'false' && detail.variantId) open();
    });
  });

  document.addEventListener('fndry:cart:refresh', function () {
    if (suppressRefresh) return; /* our own removal already rendered from the response */
    refresh();
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });

  /* ---------- theme editor ------------------------------------------------ */

  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.querySelector('[data-fndry-cart-drawer]')) open();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    if (event.target.querySelector('[data-fndry-cart-drawer]')) close();
  });

  document.addEventListener('shopify:section:load', function (event) {
    var r = event.target.querySelector('[data-fndry-cart-drawer]');
    if (!r) return;
    var c = r.querySelector('[data-fcd-content]');
    if (c) syncBubble(parseInt(c.dataset.itemCount, 10) || 0);
    document.body.classList.remove('fndry-no-scroll');
  });
})();
